import type { FacebookPhoto, FacebookPost } from "@/content/facebookPosts";
import {
  POST_OVERRIDES,
  type PhotoCaption,
  type PhotoCredit,
} from "@/content/postOverrides";

/**
 * The post shape that pages see after overrides are merged in. On top of
 * the generated FacebookPost, two optional fields are surfaced purely from
 * the override layer:
 *   - photoCaptions: per-photo bilingual captions (triggers photo-essay
 *                    layout on PostDetail).
 *   - photoCredits:  attribution for stock / Wikimedia images paired by
 *                    index with the photos array. `undefined` at a given
 *                    index means no credit is required (original FB photo
 *                    from the archive).
 */
export interface RenderablePost extends FacebookPost {
  photoCaptions?: readonly PhotoCaption[];
  photoCredits?: readonly (PhotoCredit | undefined)[];
}

/**
 * Merge a post with its per-post override (if any) and return the rendered
 * form. Pages read through this helper instead of FACEBOOK_POSTS directly so
 * hand-edits in src/content/postOverrides.ts take effect without regenerating
 * facebookPosts.ts.
 *
 * Behaviour for the `photos` override field:
 *   - `removedPhotos` is applied first against the BASE post's photos array,
 *     filtering out any indices the editor has marked hidden. Underlying
 *     JPEGs in public/facebook-media/ are NOT deleted; only the rendered
 *     list is filtered.
 *   - If the post still has photos after filtering, the override.photos
 *     field is IGNORED — Facebook-archive imagery always wins. This
 *     prevents an editorial stock image from silently replacing real
 *     archive content that might later be rediscovered.
 *   - If the post has no photos left (originally empty, or every base
 *     photo was removed), override.photos is lifted into the photos
 *     array (mapped into the FacebookPhoto shape) and the attribution
 *     list is surfaced as photoCredits.
 *
 * `photoCaptions` is paired by index with the FINAL `photos` array (after
 * filtering / overlay), so the editor must compose captions in the same
 * post-filter order it intends them to render.
 */
export function mergePost(post: FacebookPost): RenderablePost {
  const override = POST_OVERRIDES[post.slug];
  if (!override) return post;

  let photos: FacebookPhoto[] = post.photos;
  if (override.removedPhotos && override.removedPhotos.length > 0) {
    const removed = new Set(override.removedPhotos);
    photos = photos.filter((_, i) => !removed.has(i));
  }

  let photoCredits: (PhotoCredit | undefined)[] | undefined;
  if (photos.length === 0 && override.photos && override.photos.length > 0) {
    photos = override.photos.map((p) => ({
      full: `/stock/${p.src}-full.jpg`,
      thumb: `/stock/${p.src}-thumb.jpg`,
      alt: p.alt,
    }));
    photoCredits = override.photos.map((p) => p.credit);
  }

  return {
    ...post,
    titleAr: override.titleAr ?? post.titleAr,
    titleEn: override.titleEn ?? post.titleEn,
    textAr: override.textAr ?? post.textAr,
    textEn: override.textEn ?? post.textEn,
    photos,
    photoCaptions: override.photoCaptions,
    photoCredits,
  };
}

export function isOverrideExcluded(slug: string): boolean {
  return POST_OVERRIDES[slug]?.exclude === true;
}
